import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { VenueSectionData } from '@/types/unifiedGameForm';
import { GameWithDetails } from '@/types/gameSystem';
import { GameCodeSystem } from '../interfaces/GameCodeSystem';
import { PlayerReadinessManager } from './PlayerReadinessManager';
import { TeamChangeDialog } from '../dialogs/TeamChangeDialog';
import { TeamChangeAuditDialog } from '../dialogs/TeamChangeAuditDialog';
import { Building2, Loader2, Save, RefreshCw, History } from 'lucide-react';
import { COMMON_TIMEZONES, getUserTimezone } from '@/utils/timezone';

interface VenueSectionProps {
  gameData: GameWithDetails;
  canEdit: boolean;
  canApprove: boolean;
  onSave: (data: VenueSectionData) => void;
  saving: boolean;
}

export const VenueSection: React.FC<VenueSectionProps> = ({
  gameData,
  canEdit,
  canApprove,
  onSave,
  saving
}) => {
  // Get actual team names from gameData relationships
  const homeTeamName = (gameData as any).home_team?.team_name || 'Home Team';
  const awayTeamName = (gameData as any).away_team?.team_name || 'Away Team';
  
  // Auto-generate game name from team names
  const generateGameName = (home: string, away: string) => {
    if (home !== 'Home Team' && away !== 'Away Team') {
      return `${home} (Home) vs ${away} (Away)`;
    }
    return (gameData as any).game_title || (gameData as any).game_name || '';
  };

  const [formData, setFormData] = useState<VenueSectionData>({
    gameDetails: {
      gameName: generateGameName(homeTeamName, awayTeamName),
      gameDate: gameData.game_date || '',
      gameTime: gameData.game_time || '',
      gameTimezone: (gameData as any).game_timezone || getUserTimezone(),
      venueAddress: 'Viana Sports Complex, 123 Sports Drive, Viana, CA, 90210',
      fieldLocation: (gameData as any).field_location || '',
      gameDescription: (gameData as any).game_description || ''
    },
    teams: {
      homeTeamId: gameData.home_team_id || undefined,
      awayTeamId: gameData.away_team_id || undefined,
      homeTeamLabel: homeTeamName,
      awayTeamLabel: awayTeamName
    },
    venueInfo: {
      facilityNotes: (gameData as any).facility_notes || '',
      bookingStatus: 'confirmed',
      approvalStatus: 'pending'
    },
    playerReadiness: {}
  });

  const [changeDialogOpen, setChangeDialogOpen] = useState(false);
  const [auditDialogOpen, setAuditDialogOpen] = useState(false);
  const [selectedTeamSide, setSelectedTeamSide] = useState<'home' | 'away'>('home');

  // Update game name when team labels change
  useEffect(() => {
    const newGameName = generateGameName(formData.teams.homeTeamLabel, formData.teams.awayTeamLabel);
    if (newGameName !== formData.gameDetails.gameName && newGameName !== '') {
      setFormData(prev => ({
        ...prev,
        gameDetails: {
          ...prev.gameDetails,
          gameName: newGameName
        }
      }));
    }
  }, [formData.teams.homeTeamLabel, formData.teams.awayTeamLabel]);

  const handleInputChange = (section: keyof VenueSectionData, field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value
      }
    }));
  };

  const handleSave = () => {
    onSave(formData);
  };

  const handleChangeTeam = (side: 'home' | 'away') => {
    setSelectedTeamSide(side);
    setChangeDialogOpen(true);
  };

  const handleTeamChangeSuccess = () => {
    // Refresh the page or reload game data
    window.location.reload();
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Building2 className="h-5 w-5" />
          Venue Information
          {!canEdit && <Badge variant="outline">View Only</Badge>}
        </CardTitle>
        <CardDescription>
          Game details, venue information, and team assignments
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Game Details */}
        <div className="space-y-4">
          <h4 className="text-sm font-semibold">Game Details</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="gameName">Game Name</Label>
              <Input
                id="gameName"
                value={formData.gameDetails.gameName}
                onChange={(e) => handleInputChange('gameDetails', 'gameName', e.target.value)}
                disabled={!canEdit}
              />
            </div>
            <div>
              <Label htmlFor="gameDate">Game Date</Label>
              <Input
                id="gameDate"
                type="date"
                value={formData.gameDetails.gameDate}
                onChange={(e) => handleInputChange('gameDetails', 'gameDate', e.target.value)}
                disabled={!canEdit}
              />
            </div>
            <div>
              <Label htmlFor="gameTime">Game Time</Label>
              <Input
                id="gameTime"
                type="time"
                value={formData.gameDetails.gameTime}
                onChange={(e) => handleInputChange('gameDetails', 'gameTime', e.target.value)}
                disabled={!canEdit}
              />
            </div>
            <div>
              <Label htmlFor="gameTimezone">Timezone</Label>
              <Select
                value={formData.gameDetails.gameTimezone}
                onValueChange={(value) => handleInputChange('gameDetails', 'gameTimezone', value)}
                disabled={!canEdit}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select timezone" />
                </SelectTrigger>
                <SelectContent>
                  {COMMON_TIMEZONES.map((tz) => (
                    <SelectItem key={tz.value} value={tz.value}>
                      {tz.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label htmlFor="venueAddress">Venue Address</Label>
            <Input
              id="venueAddress"
              value={formData.gameDetails.venueAddress}
              onChange={(e) => handleInputChange('gameDetails', 'venueAddress', e.target.value)}
              disabled={!canEdit}
            />
          </div>
          <div>
            <Label htmlFor="fieldLocation">Field Location</Label>
            <Input
              id="fieldLocation"
              value={formData.gameDetails.fieldLocation || ''}
              onChange={(e) => handleInputChange('gameDetails', 'fieldLocation', e.target.value)}
              disabled={!canEdit}
              placeholder="e.g., Field A, Court 1"
            />
          </div>
          <div>
            <Label htmlFor="gameDescription">Game Description</Label>
            <Textarea
              id="gameDescription"
              value={formData.gameDetails.gameDescription || ''}
              onChange={(e) => handleInputChange('gameDetails', 'gameDescription', e.target.value)}
              disabled={!canEdit}
              placeholder="Additional game details or instructions"
            />
          </div>
        </div>

        {/* Team Assignments */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h4 className="text-sm font-semibold">Team Assignments</h4>
            {canEdit && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setAuditDialogOpen(true)}
              >
                <History className="h-4 w-4 mr-2" />
                Change History
              </Button>
            )}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="homeTeamLabel">Home Team</Label>
              <div className="flex gap-2">
                <Input
                  id="homeTeamLabel"
                  value={formData.teams.homeTeamLabel}
                  onChange={(e) => handleInputChange('teams', 'homeTeamLabel', e.target.value)}
                  disabled={!canEdit}
                  className="flex-1"
                />
                {canEdit && (
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => handleChangeTeam('home')}
                    title="Change home team"
                  >
                    <RefreshCw className="h-4 w-4" />
                  </Button>
                )}
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="awayTeamLabel">Away Team</Label>
              <div className="flex gap-2">
                <Input
                  id="awayTeamLabel"
                  value={formData.teams.awayTeamLabel}
                  onChange={(e) => handleInputChange('teams', 'awayTeamLabel', e.target.value)}
                  disabled={!canEdit}
                  className="flex-1"
                />
                {canEdit && (
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => handleChangeTeam('away')}
                    title="Change away team"
                  >
                    <RefreshCw className="h-4 w-4" />
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Venue Information */}
        <div className="space-y-4">
          <h4 className="text-sm font-semibold">Venue Information</h4>
          <div>
            <Label htmlFor="facilityNotes">Facility Notes</Label>
            <Textarea
              id="facilityNotes"
              value={formData.venueInfo.facilityNotes}
              onChange={(e) => handleInputChange('venueInfo', 'facilityNotes', e.target.value)}
              disabled={!canEdit}
              placeholder="Parking information, facility rules, equipment notes"
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="bookingStatus">Booking Status</Label>
              <Badge variant={formData.venueInfo.bookingStatus === 'confirmed' ? 'default' : 'secondary'}>
                {formData.venueInfo.bookingStatus}
              </Badge>
            </div>
            <div>
              <Label htmlFor="approvalStatus">Approval Status</Label>
              <Badge variant={formData.venueInfo.approvalStatus === 'approved' ? 'default' : 'secondary'}>
                {formData.venueInfo.approvalStatus}
              </Badge>
            </div>
          </div>
        </div>

        {/* Game Code System */}
        <div className="space-y-4">
          <h4 className="text-sm font-semibold">Game Access</h4>
          <GameCodeSystem
            gameId={(gameData as any).id}
            gameType="venue" // Determine this based on game type
            gameCode={gameData.game_code}
            accessLevel={(gameData as any).game_code_access_level || 'open'}
            onAccessLevelChange={(level) => {
              // Handle access level change
              console.log('Access level changed to:', level);
            }}
          />
        </div>


        {/* Actions */}
        {canEdit && (
          <div className="flex justify-end">
            <Button onClick={handleSave} disabled={saving}>
              {saving ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  Save Venue Information
                </>
              )}
            </Button>
          </div>
        )}
      </CardContent>

      {/* Team Change Dialog */}
      <TeamChangeDialog
        open={changeDialogOpen}
        onOpenChange={setChangeDialogOpen}
        gameId={(gameData as any).id}
        gameType="regular"
        teamSide={selectedTeamSide}
        currentTeamId={selectedTeamSide === 'home' ? formData.teams.homeTeamId || null : formData.teams.awayTeamId || null}
        currentTeamName={selectedTeamSide === 'home' ? formData.teams.homeTeamLabel : formData.teams.awayTeamLabel}
        onSuccess={handleTeamChangeSuccess}
      />

      {/* Team Change Audit Dialog */}
      <TeamChangeAuditDialog
        open={auditDialogOpen}
        onOpenChange={setAuditDialogOpen}
        gameId={(gameData as any).id}
        gameType="regular"
      />
    </Card>
  );
};